/**
 * StructuredResponse Component
 * Main component for rendering structured AI responses
 * Supports both legacy format and new enhanced ResultsLayout
 */

import React from 'react';
import { CardGrid, Card } from './CardGrid';
import { SectionsList, SectionData } from './Section';
import { TipsList, TipData } from './Tip';
import { ResultsLayout, Section as LayoutSection, ResultItem, Tip as LayoutTip } from '../results/ResultsLayout';

interface StructuredResponseData {
  title?: string;
  description?: string;
  summary?: string;
  cards?: Card[];
  sections?: SectionData[];
  tips?: TipData[];
  content?: string;
  trustScore?: number;
}

/**
 * Props for StructuredResponse component
 */
interface StructuredResponseProps {
  /**
   * Structured response data containing title, description, summary, cards, sections, and tips.
   * Automatically determines whether to use the new ResultsLayout or legacy layout.
   */
  data: StructuredResponseData;
}

/**
 * Convert legacy card format to ResultItem format
 */
function convertCardsToResultItems(cards: Card[]): ResultItem[] {
  return cards.map(card => ({
    title: card.title,
    description: card.description || card.content || '',
    icon: card.icon,
    url: card.link,
    trustScore: undefined,
    tags: undefined,
    actions: card.link ? [{
      label: 'Visitar',
      onClick: () => window.open(card.link, '_blank', 'noopener,noreferrer'),
      variant: 'outline' as const,
      icon: '→',
    }] : undefined,
  }));
}

/**
 * Convert legacy sections to new Section format with items
 */
function convertSectionsToLayout(sections: SectionData[], cards?: Card[]): LayoutSection[] {
  const layoutSections: LayoutSection[] = [];
  
  // If we have sections, each becomes a section with its content
  sections.forEach(section => {
    layoutSections.push({
      title: section.title,
      icon: section.icon,
      items: [{
        title: section.title,
        description: section.content,
      }],
    });
  });
  
  return layoutSections;
}

/**
 * Convert legacy tips to new Tip format
 */
function convertTipsToLayout(tips: TipData[]): LayoutTip[] {
  return tips.map(tip => ({
    text: tip.message,
    type: tip.variant === 'error' ? 'warning' : 
          tip.variant === 'info' ? 'info' :
          tip.variant === 'success' ? 'success' : 'tip',
  }));
}

/**
 * Determine if we should use the new ResultsLayout
 * Use it when we have summary, or multiple cards, or sections with meaningful structure
 */
function shouldUseResultsLayout(data: StructuredResponseData): boolean {
  // Use ResultsLayout if we have a summary
  if (data.summary) return true;
  
  // Use ResultsLayout if we have multiple cards (suggests a structured response)
  if (data.cards && data.cards.length >= 3) return true;
  
  // Use ResultsLayout if we have sections AND cards (combo suggests structured layout)
  if (data.sections && data.sections.length > 0 && data.cards && data.cards.length > 0) {
    return true;
  }
  
  return false;
}

export const StructuredResponse: React.FC<StructuredResponseProps> = ({ data }) => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  // Determine which layout to use
  const useResultsLayout = shouldUseResultsLayout(data);

  // Use new ResultsLayout when appropriate
  if (useResultsLayout) {
    const sections: LayoutSection[] = [];
    
    // If we have cards, create a section for them
    if (data.cards && data.cards.length > 0) {
      sections.push({
        title: data.title || 'Resultados',
        icon: '🔍',
        items: convertCardsToResultItems(data.cards),
      });
    }
    
    // Add any additional sections
    if (data.sections && data.sections.length > 0) {
      sections.push(...convertSectionsToLayout(data.sections));
    }
    
    const layoutTips = data.tips ? convertTipsToLayout(data.tips) : undefined;
    
    return (
      <ResultsLayout
        summary={data.summary || data.description}
        summaryIcon="🌴"
        sections={sections}
        tips={layoutTips}
      />
    );
  }

  // Use legacy layout for simpler structures
  return (
    <div className="ai360-structured-response">
      {data.title && (
        <h2 className="ai360-structured-title">{data.title}</h2>
      )}
      
      {data.description && (
        <p className="ai360-structured-description">{data.description}</p>
      )}

      {data.tips && data.tips.length > 0 && (
        <TipsList tips={data.tips} />
      )}

      {data.cards && data.cards.length > 0 && (
        <CardGrid cards={data.cards} columns={2} />
      )}

      {data.sections && data.sections.length > 0 && (
        <SectionsList sections={data.sections} />
      )}

      {data.content && (
        <div className="ai360-structured-content">
          <p>{data.content}</p>
        </div>
      )}
    </div>
  );
};
